/**
 * ==================== ADMIN CHANGE PASSWORD ====================
 * Página para alteração de senha do administrador.
 * SAFE PATCH: Não modifica sistema de autenticação existente.
 * ==============================================================
 */
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, Loader2, CheckCircle, Eye, EyeOff, AlertCircle, KeyRound } from 'lucide-react';

export default function AdminChangePassword() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
    }
  }, [user, navigate]);

  const validate = () => {
    const newErrors = {};
    
    if (!form.current_password) {
      newErrors.current_password = 'Senha atual é obrigatória';
    }
    
    if (!form.new_password) {
      newErrors.new_password = 'Nova senha é obrigatória';
    } else if (form.new_password.length < 8) {
      newErrors.new_password = 'Nova senha deve ter pelo menos 8 caracteres';
    }
    
    if (!form.confirm_password) {
      newErrors.confirm_password = 'Confirmação de senha é obrigatória';
    } else if (form.new_password !== form.confirm_password) {
      newErrors.confirm_password = 'As senhas não coincidem';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/change-password`, {
        current_password: form.current_password,
        new_password: form.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(true);
      toast.success('Senha alterada com sucesso!');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
      
      // Redirect após 2 segundos
      setTimeout(() => {
        navigate('/control-portal-clube/dashboard');
      }, 2000);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao alterar senha';
      toast.error(detail);
      
      if (detail.includes('atual')) {
        setErrors({ current_password: detail });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.user_type !== 'admin') {
    return null;
  }

  return (
    <AdminLayout title="Alterar Senha" subtitle="Atualize sua senha de acesso">
      <div className="max-w-md mx-auto">
        {success ? (
          <Card className="p-8 text-center">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-green-700 mb-2">Senha Alterada!</h2>
            <p className="text-slate-500 mb-4">Sua senha foi atualizada com sucesso.</p>
            <p className="text-sm text-slate-400">Redirecionando para o dashboard...</p>
          </Card>
        ) : (
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#2563EB]/10 p-3 rounded-lg">
                <KeyRound className="w-6 h-6 text-[#2563EB]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#0B1F3A]">Alterar Senha</h2>
                <p className="text-sm text-slate-500">Defina uma nova senha para sua conta</p>
              </div>
            </div>
            
            <Alert className="mb-6 border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700 text-sm">
                A nova senha deve ter pelo menos 8 caracteres.
              </AlertDescription>
            </Alert>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Senha Atual */}
              <div>
                <Label htmlFor="current_password">Senha atual</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    placeholder="Digite sua senha atual"
                    className={`pl-10 pr-10 ${errors.current_password ? 'border-red-500' : ''}`}
                    value={form.current_password}
                    onChange={(e) => {
                      setForm({ ...form, current_password: e.target.value });
                      setErrors({ ...errors, current_password: '' });
                    }}
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.current_password && (
                  <p className="text-red-500 text-xs mt-1">{errors.current_password}</p>
                )}
              </div>
              
              {/* Nova Senha */}
              <div>
                <Label htmlFor="new_password">Nova senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="new_password"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Digite a nova senha"
                    className={`pl-10 pr-10 ${errors.new_password ? 'border-red-500' : ''}`}
                    value={form.new_password}
                    onChange={(e) => {
                      setForm({ ...form, new_password: e.target.value });
                      setErrors({ ...errors, new_password: '' });
                    }}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.new_password && (
                  <p className="text-red-500 text-xs mt-1">{errors.new_password}</p>
                )}
                {form.new_password && form.new_password.length >= 8 && (
                  <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Senha válida
                  </p>
                )}
              </div>
              
              {/* Confirmar Nova Senha */}
              <div>
                <Label htmlFor="confirm_password">Confirmar nova senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="confirm_password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirme a nova senha"
                    className={`pl-10 pr-10 ${errors.confirm_password ? 'border-red-500' : ''}`}
                    value={form.confirm_password}
                    onChange={(e) => {
                      setForm({ ...form, confirm_password: e.target.value });
                      setErrors({ ...errors, confirm_password: '' });
                    }}
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirm_password && (
                  <p className="text-red-500 text-xs mt-1">{errors.confirm_password}</p>
                )}
                {form.confirm_password && form.new_password === form.confirm_password && (
                  <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Senhas coincidem
                  </p>
                )}
              </div>
              
              <div className="pt-4 space-y-3">
                <Button 
                  type="submit" 
                  className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90" 
                  disabled={loading}
                  data-testid="change-password-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Atualizando...
                    </>
                  ) : (
                    <>
                      <KeyRound className="w-4 h-4 mr-2" />
                      Atualizar senha
                    </>
                  )}
                </Button>
                
                <Button 
                  type="button"
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/control-portal-clube/dashboard')}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
